/**
 * @file Prescriptions.js
 * @version 1.6.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-04-17
 * @modified 2026-10-08
 * @description Search and manage veterinary prescriptions for customers
 */

import React, { useState, useEffect, useMemo } from 'react';
import { API_URL } from '../config';
import './SearchLayout.css';
import './Prescriptions.css';
import './TableStyles.css';
import Dropdown from './Dropdown';
import Input from './Input';
import PaginationControls from './PaginationControls';
import LoadingSpinner from './LoadingSpinner';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import PrescriptionModal from './PrescriptionModal';
import ContactModal from './ContactModal';
import RowActionsMenu from './RowActionsMenu';
import TableContextMenu from './TableContextMenu';
import { useSlideOutButton } from './SlideOutButton';
import { formatDateShort } from '../utils/dateFormatters';
import usePersistedState from '../hooks/usePersistedState';
import { matchesAllTerms } from '../utils/searchMatch';
import SearchHelp from './SearchHelp';
import ColumnFilterNote from './ColumnFilterNote';
import ColumnFilterMenu from './ColumnFilterMenu';
import useColumnFilters from '../hooks/useColumnFilters';
import useTableContextMenu from '../hooks/useTableContextMenu';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import { prescriptionStatusDisplay, isOverPrescribed, overPrescribedRowClass, overPrescribedTitle } from '../utils/prescriptions';
import { formatQuantity } from '../utils/numberFormatters';

/*
 * What Clear Filters resets to — and so what "no filters applied" means for
 * the button's highlight (utils/activeFilters.js). The screen opens on Valid,
 * which is a filter, so it lights the button until cleared.
 */
const CLEARED_FILTERS = { search: '', status: '', showArchived: false, limit: '50' };

/*
 * Right-click a column header to narrow on its values. The list is small
 * enough to be held whole, so the filter runs over every loaded row (after the
 * search), not just the page on screen.
 */
const COLUMN_FILTERS = {
  customer: { label: 'Customer', value: (rx) => rx.customer_name },
  product: { label: 'Product', value: (rx) => rx.product_name },
  vet_ref: { label: 'Vet Ref', value: (rx) => rx.vet_reference },
  status: { label: 'Status', value: (rx) => prescriptionStatusDisplay(rx).label }
};

function Prescriptions({ user, onNavigate }) {
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [modalMode, setModalMode] = useState('view');
  const [selectedContactId, setSelectedContactId] = usePersistedState('prescriptions.contactModalId', null);
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null });

  // Slideout buttons
  useSlideOutButton();

  // Opens on Valid: of 253 prescriptions only a handful are current at any one
  // time, and the question this screen usually answers is "which are in force".
  // A tab that has already chosen a filter keeps its choice — this is only the
  // starting point.
  const [filters, setFilters] = usePersistedState('prescriptions.filters', {
    search: '',
    // All statuses by default (Cam, 2026-09-29) — the same as Clear Filters,
    // so the list opens unfiltered and the Clear Filters button unlit.
    status: '',
    showArchived: false,
    limit: '50'
  });

  const [sorting, setSorting] = usePersistedState('prescriptions.sorting', {
    field: 'status',
    direction: 'asc'
  });

  const [pagination, setPagination] = usePersistedState('prescriptions.pagination', {
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  useEffect(() => {
    loadPrescriptions();
  }, []);

  useEffect(() => {
    const delayTimer = setTimeout(() => {
      loadPrescriptions();
    }, 300);
    return () => clearTimeout(delayTimer);
  }, [filters.status, filters.showArchived, filters.limit]);

  const loadPrescriptions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: 10000,
        sortField: sorting.field,
        sortDirection: sorting.direction
      });

      if (filters.status) params.append('status', filters.status);
      if (filters.showArchived) params.append('showArchived', 'true');

      const response = await fetch(`${API_URL}/api/prescriptions/search?${params}`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setPrescriptions(data.prescriptions || []);
        const limit = parseInt(filters.limit) || 50;
        setPagination({
          page: 1,
          limit,
          total: (data.prescriptions || []).length,
          totalPages: Math.ceil((data.prescriptions || []).length / limit)
        });
      }
    } catch (error) {
      console.error('Error loading prescriptions:', error);
      setAlertDialog({ isOpen: true, message: 'Failed to load prescriptions', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSort = (field) => {
    setSorting(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Client-side filtering and sorting
  // Every word, in any order; "quoted phrases" kept together
  const searchedPrescriptions = useMemo(() => (filters.search
    ? prescriptions.filter(rx => matchesAllTerms([
      rx.customer_name,
      rx.account_no,
      rx.product_name,
      rx.product_code,
      rx.vet_reference,
      rx.prescription_number
    ], filters.search))
    : prescriptions), [prescriptions, filters.search]);

  const cols = useColumnFilters(searchedPrescriptions, COLUMN_FILTERS, {
    storageKey: 'prescriptions.columnFilters'
  });

  const filteredAndSortedPrescriptions = useMemo(() => {
    const filtered = [...cols.rows];

    // Sort: status first (Valid before Expired), then by secondary field
    filtered.sort((a, b) => {
      let aVal = a[sorting.field];
      let bVal = b[sorting.field];

      // Handle nulls
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';

      // Numeric comparison for tonnes
      if (['prescribed_tonnes', 'remaining_tonnes'].includes(sorting.field)) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (['from_date', 'to_date'].includes(sorting.field)) {
        // Date comparison
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      } else if (sorting.field === 'status') {
        // Custom sort: Valid = 0, Expired = 1, Archived = 2
        const statusOrder = { 'Valid': 0, 'Expired': 1, 'Archived': 2 };
        aVal = statusOrder[a.is_archived ? 'Archived' : a.status] ?? 1;
        bVal = statusOrder[b.is_archived ? 'Archived' : b.status] ?? 1;
      } else {
        aVal = aVal.toString().toLowerCase();
        bVal = bVal.toString().toLowerCase();
      }

      if (aVal < bVal) return sorting.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sorting.direction === 'asc' ? 1 : -1;

      // Secondary sort: from_date desc
      const aDate = a.from_date ? new Date(a.from_date).getTime() : 0;
      const bDate = b.from_date ? new Date(b.from_date).getTime() : 0;
      return bDate - aDate;
    });

    return filtered;
  }, [cols.rows, sorting]);

  // Update pagination when filters change
  useEffect(() => {
    const limit = parseInt(filters.limit) || 50;
    setPagination(prev => ({
      ...prev,
      page: 1,
      limit,
      total: filteredAndSortedPrescriptions.length,
      totalPages: Math.ceil(filteredAndSortedPrescriptions.length / limit)
    }));
  }, [filteredAndSortedPrescriptions.length, filters.search, filters.limit, cols.queryValue]);

  // Paginate
  const paginatedPrescriptions = filteredAndSortedPrescriptions.slice(
    (pagination.page - 1) * pagination.limit,
    pagination.page * pagination.limit
  );

  const ctxMenu = useTableContextMenu({
    rows: paginatedPrescriptions,
    getRowId: (rx) => rx.id
  });

  const changePage = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const handleClearFilters = () => {
    setFilters(prev => ({ ...CLEARED_FILTERS, limit: prev.limit || '50' }));
    setSorting({ field: 'status', direction: 'asc' });
    cols.clearEverything();
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Any filter at all — header or column — lights the Clear Filters button.
  const headerFilters = activeHeaderFilters(filters, CLEARED_FILTERS);
  const filtersApplied = headerFilters.count > 0 || cols.activeCount > 0;

  const handleAddNew = () => {
    setSelectedPrescription(null);
    setModalMode('add');
    setShowModal(true);
  };

  const handleView = (rx) => {
    setSelectedPrescription(rx);
    setModalMode('view');
    setShowModal(true);
  };

  const handleEdit = (rx) => {
    setSelectedPrescription(rx);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleArchive = (rx) => {
    setConfirmDialog({
      isOpen: true,
      message: `Are you sure you want to archive prescription ${rx.prescription_number} for ${rx.customer_name}?`,
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        try {
          const response = await fetch(`${API_URL}/api/prescriptions/${rx.id}/archive`, {
            method: 'PUT',
            credentials: 'include'
          });

          if (response.ok) {
            setAlertDialog({ isOpen: true, message: 'Prescription archived', type: 'success' });
            loadPrescriptions();
          } else {
            setAlertDialog({ isOpen: true, message: 'Failed to archive prescription', type: 'error' });
          }
        } catch (error) {
          console.error('Error archiving prescription:', error);
          setAlertDialog({ isOpen: true, message: 'Error archiving prescription', type: 'error' });
        }
      }
    });
  };

  const handleSave = () => {
    setShowModal(false);
    loadPrescriptions();
  };

  const buildRowActions = (rx) => [
    { key: 'view', icon: 'visibility', label: 'View', title: 'View', onClick: () => handleView(rx) },
    { key: 'edit', icon: 'edit', label: 'Edit', title: 'Edit', onClick: () => handleEdit(rx) },
    {
      key: 'archive',
      icon: 'archive',
      label: 'Archive',
      disabled: rx.status !== 'Expired' || rx.is_archived,
      title: rx.is_archived ? 'Already archived' : rx.status !== 'Expired' ? 'Only expired prescriptions can be archived' : 'Archive',
      onClick: () => {
        if (rx.status === 'Expired' && !rx.is_archived) handleArchive(rx);
      }
    },
  ];

  // Shared with the contact modal's Prescriptions panel — see utils/prescriptions.
  const getStatusDisplay = prescriptionStatusDisplay;

  // Summary stats

  return (
    <div className="prescriptions">
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">
            Manage veterinary prescriptions for customers
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button
                className={clearFiltersClass(filtersApplied)}
                onClick={handleClearFilters}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">filter_alt_off</span>
                </div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" onClick={handleAddNew}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">add</span>
                </div>
                <span className="btn-label">Add Prescription</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="search-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>
              Search
              <SearchHelp fields={['customer', 'account no.', 'product', 'product code', 'vet reference', 'prescription no.']} />
            </label>
            <Input
              type="text"
              placeholder="Customer, product, vet ref, prescription ID..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              icon="search"
            />
          </div>

          <div className="filter-group">
            <label>Status</label>
            <Dropdown
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
              options={[
                { value: '', label: 'All' },
                { value: 'Valid', label: 'Valid' },
                { value: 'Expired', label: 'Expired' }
              ]}
            />
          </div>

          <div className="filter-group">
            <label>Show Archived</label>
            <div className="checkbox-container">
              <div className="check light">
                <input
                  id="show-archived"
                  type="checkbox"
                  checked={filters.showArchived}
                  onChange={(e) => handleFilterChange('showArchived', e.target.checked)}
                />
                <label htmlFor="show-archived"></label>
              </div>
            </div>
          </div>

          <div className="filter-group">
            <label>Per Page</label>
            <Dropdown
              value={filters.limit}
              onChange={(value) => handleFilterChange('limit', value)}
              options={[
                { value: '20', label: '20' },
                { value: '50', label: '50' },
                { value: '100', label: '100' },
                { value: '200', label: '200' }
              ]}
              placeholder="50"
            />
          </div>
        </div>
      </div>

      <div className="prescriptions-panel">
        {/* Filter pill (when anything narrows the list), then "Showing…" and
            the page controls taking the rest of the row. */}
        <div className="table-toolbar">
          <ColumnFilterNote cols={cols} header={headerFilters} onClear={handleClearFilters} />
          {pagination.totalPages > 0 && (
            <PaginationControls
              pagination={pagination}
              onPageChange={changePage}
            />
          )}
        </div>

        <div className="table-container">
          {loading ? (
            <LoadingSpinner />
          ) : (
            <table className="data-table" onContextMenu={ctxMenu.onTableContextMenu}>
              <thead>
                <tr>
                  <th className={`sortable ${cols.headerProps('customer').className || ''}`.trim()}
                      onClick={() => handleSort('customer_name')}
                      onContextMenu={cols.headerProps('customer').onContextMenu}>
                    Customer {sorting.field === 'customer_name' && (sorting.direction === 'asc' ? '\u25B2' : '\u25BC')}
                  </th>
                  <th className={`sortable ${cols.headerProps('product').className || ''}`.trim()}
                      onClick={() => handleSort('product_name')}
                      onContextMenu={cols.headerProps('product').onContextMenu}>
                    Product {sorting.field === 'product_name' && (sorting.direction === 'asc' ? '\u25B2' : '\u25BC')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('prescription_number')}>
                    Prescription ID {sorting.field === 'prescription_number' && (sorting.direction === 'asc' ? '\u25B2' : '\u25BC')}
                  </th>
                  <th {...cols.headerProps('vet_ref')}>Vet Ref</th>
                  <th className="text-right sortable" onClick={() => handleSort('prescribed_tonnes')}>
                    Tonnes {sorting.field === 'prescribed_tonnes' && (sorting.direction === 'asc' ? '\u25B2' : '\u25BC')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('from_date')}>
                    From {sorting.field === 'from_date' && (sorting.direction === 'asc' ? '\u25B2' : '\u25BC')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('to_date')}>
                    To {sorting.field === 'to_date' && (sorting.direction === 'asc' ? '\u25B2' : '\u25BC')}
                  </th>
                  <th className={`status-col no-sort ${cols.headerProps('status').className || ''}`.trim()}
                      onContextMenu={cols.headerProps('status').onContextMenu}>Status</th>
                  <th className="actions-col no-sort"></th>
                </tr>
              </thead>
              <tbody>
                {paginatedPrescriptions.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center">
                      No prescriptions found
                    </td>
                  </tr>
                ) : (
                  paginatedPrescriptions.map((rx) => {
                    const statusDisplay = getStatusDisplay(rx);
                    const isOver = isOverPrescribed(rx);
                    const overTitle = overPrescribedTitle(rx);
                    const rowClasses = [
                      'rx-row',
                      rx.is_archived ? 'inactive-row' : '',
                      overPrescribedRowClass(rx)
                    ].filter(Boolean).join(' ');
                    return (
                      <tr
                        key={rx.id}
                        className={rowClasses}
                        onClick={() => handleView(rx)}
                        title="View prescription"
                        onContextMenu={ctxMenu.onRowContextMenu(rx)}
                      >
                        <td>
                          <span className="name-with-badge">
                            <span
                              className={`account-badge-inline${!rx.account_no ? ' empty' : ''}`}
                              style={(rx.account_id && rx.account_no) ? { cursor: 'pointer' } : undefined}
                              onClick={(rx.account_id && rx.account_no) ? (e) => { e.stopPropagation(); setSelectedContactId(rx.account_id); } : undefined}
                            >
                              {rx.account_no || '\u00A0'}
                            </span>
                            <span
                              className="contact-link"
                              onClick={(e) => { e.stopPropagation(); if (rx.account_id) setSelectedContactId(rx.account_id); }}
                            >
                              {rx.customer_name}
                            </span>
                          </span>
                        </td>
                        <td>
                          <div>{rx.product_name}</div>
                          {rx.product_code && (
                            <div className="product-code">{rx.product_code}</div>
                          )}
                        </td>
                        <td>{rx.prescription_number}</td>
                        <td>{rx.vet_reference || '-'}</td>
                        <td className="text-right">
                          <div className="tonnes-cell">
                            {isOver && (
                              <span
                                className="material-symbols-rounded over-prescribed-icon"
                                title={overTitle}
                              >
                                warning
                              </span>
                            )}
                            <span>{formatQuantity(parseFloat(rx.prescribed_tonnes || 0))}</span>
                          </div>
                          <div className="remaining-tonnes">({formatQuantity(parseFloat(rx.remaining_tonnes || 0))} left)</div>
                        </td>
                        <td>{formatDateShort(rx.from_date)}</td>
                        <td>{formatDateShort(rx.to_date)}</td>
                        <td className="status-col">
                          <span className={`status-badge ${statusDisplay.className}`}>
                            {statusDisplay.label}
                          </span>
                        </td>
                        <td>
                          <div className="actions-col" onClick={(e) => e.stopPropagation()}>
                            <RowActionsMenu actions={buildRowActions(rx)} />
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <PrescriptionModal
          prescription={selectedPrescription}
          mode={modalMode}
          onClose={() => { setShowModal(false); loadPrescriptions(); }}
          onSave={handleSave}
        />
      )}

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />

      {selectedContactId && (
        <ContactModal
          contactId={selectedContactId}
          onClose={() => setSelectedContactId(null)}
        />
      )}

      <ColumnFilterMenu cols={cols} />

      <TableContextMenu
        ctx={ctxMenu}
        rowActions={buildRowActions}
        actions={[
          { key: 'add', icon: 'add', label: 'Add prescription', onClick: handleAddNew },
          { key: 'refresh', icon: 'refresh', label: 'Refresh', onClick: loadPrescriptions },
          {
            key: 'show-archived',
            icon: 'inventory_2',
            label: 'Show archived',
            active: filters.showArchived,
            onClick: () => handleFilterChange('showArchived', !filters.showArchived)
          },
          { key: 'clear-filters', icon: 'filter_alt_off', label: 'Clear filters', onClick: handleClearFilters }
        ]}
      />
    </div>
  );
}

export default Prescriptions;
